import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';

const VERSION = 'v1.0.0';

const styles = `
  .analytics-section {
    margin-bottom: 32px;
  }
  .analytics-section h2 {
    font-size: 1rem;
    font-weight: 600;
    color: #2C2C2C;
    margin-bottom: 14px;
    letter-spacing: -0.01em;
  }
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
  }
  .stat-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  }
  .stat-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #2C2C2C;
    letter-spacing: -0.03em;
    margin-top: 4px;
  }
  .stat-sub {
    font-size: 0.8rem;
    color: #888;
    margin-top: 6px;
  }
  .bar-chart {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  }
  .bar-chart-title {
    font-size: 0.8rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 16px;
  }
  .bar-chart-bars {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    height: 120px;
  }
  .bar-chart-bar {
    flex: 1;
    background: #E8845A;
    border-radius: 3px 3px 0 0;
    min-width: 6px;
    position: relative;
    transition: background 0.15s;
    cursor: default;
  }
  .bar-chart-bar:hover {
    background: #D06A3A;
  }
  .bar-chart-bar:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #2C2C2C;
    color: #fff;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 0.7rem;
    white-space: nowrap;
    margin-bottom: 4px;
    pointer-events: none;
  }
  .bar-chart-labels {
    display: flex;
    gap: 4px;
    margin-top: 6px;
  }
  .bar-chart-labels span {
    flex: 1;
    text-align: center;
    font-size: 0.6rem;
    color: #aaa;
    min-width: 6px;
    overflow: hidden;
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: hidden;
  }
  .data-table th {
    padding: 12px 16px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #EDE9E4;
    background: #FAFAF8;
    text-align: left;
  }
  .data-table td {
    padding: 13px 16px;
    font-size: 0.9rem;
    color: #2C2C2C;
    border-bottom: 1px solid #F0EDE8;
  }
  .data-table tr:hover td {
    background: #FDFCFB;
  }
  .badge {
    padding: 3px 8px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .badge-positive { background: #F0FFF4; color: #27AE60; }
  .badge-neutral { background: #FFF8E1; color: #F59E0B; }
  .badge-negative { background: #FFF0EE; color: #C0392B; }
  .analytics-loading {
    text-align: center;
    padding: 60px 0;
    color: #888;
    font-size: 0.95rem;
  }
  .analytics-error {
    text-align: center;
    padding: 40px 0;
    color: #C0392B;
    font-size: 0.9rem;
  }
  .analytics-version {
    text-align: right;
    font-size: 0.7rem;
    color: #ccc;
    margin-top: 8px;
  }
`;

function fmtDuration(sec) {
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function fmtHours(sec) {
  const h = sec / 3600;
  return h >= 1 ? `${h.toFixed(1)}h` : `${Math.round(sec / 60)}m`;
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminApi.analytics()
      .then(setData)
      .catch(e => setError(e.message));
  }, []);

  if (error) return (
    <AdminLayout title="Analytics">
      <style>{styles}</style>
      <div className="analytics-error">Failed to load analytics: {error}</div>
    </AdminLayout>
  );

  if (!data) return (
    <AdminLayout title="Analytics">
      <style>{styles}</style>
      <div className="analytics-loading">Loading analytics...</div>
    </AdminLayout>
  );

  const { engagement, call_quality, mood, per_user } = data;
  const maxCalls = Math.max(...engagement.calls_per_day.map(d => d.count), 1);

  return (
    <AdminLayout title="Analytics">
      <style>{styles}</style>

      {/* Engagement */}
      <div className="analytics-section">
        <h2>Engagement</h2>
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-label">Total Calls</div>
            <div className="stat-value">{engagement.total_calls.toLocaleString()}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Avg Duration</div>
            <div className="stat-value">{fmtDuration(engagement.avg_duration_sec)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Talk Time</div>
            <div className="stat-value">{fmtHours(engagement.total_talk_time_sec)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Active Users</div>
            <div className="stat-value">{engagement.active_users}</div>
          </div>
        </div>

        {engagement.calls_per_day.length > 0 && (
          <div className="bar-chart">
            <div className="bar-chart-title">Calls per Day (Last 30 Days)</div>
            <div className="bar-chart-bars">
              {engagement.calls_per_day.map(d => (
                <div
                  key={d.date}
                  className="bar-chart-bar"
                  style={{ height: `${(d.count / maxCalls) * 100}%` }}
                  data-tooltip={`${d.date}: ${d.count}`}
                />
              ))}
            </div>
            <div className="bar-chart-labels">
              {engagement.calls_per_day.map((d, i) => (
                <span key={d.date}>
                  {i === 0 || i === engagement.calls_per_day.length - 1
                    ? d.date.slice(5)
                    : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Call Quality */}
      <div className="analytics-section">
        <h2>Call Quality</h2>
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-label">User Hangup</div>
            <div className="stat-value">{call_quality.user_hangup_pct}%</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Idle Timeout</div>
            <div className="stat-value">{call_quality.idle_timeout_pct}%</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Error</div>
            <div className="stat-value">{call_quality.error_pct}%</div>
          </div>
        </div>
      </div>

      {/* Mood Trends */}
      <div className="analytics-section">
        <h2>Mood Trends (Last 30 Days)</h2>
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-label">Positive</div>
            <div className="stat-value" style={{ color: '#27AE60' }}>{mood.positive_pct}%</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Neutral</div>
            <div className="stat-value" style={{ color: '#F59E0B' }}>{mood.neutral_pct}%</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Negative</div>
            <div className="stat-value" style={{ color: '#C0392B' }}>{mood.negative_pct}%</div>
          </div>
        </div>
      </div>

      {/* Per-User Table */}
      <div className="analytics-section">
        <h2>Per-User Summary</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Total Calls</th>
              <th>Avg Duration</th>
              <th>Last Active</th>
              <th>Mood</th>
            </tr>
          </thead>
          <tbody>
            {per_user.map(u => (
              <tr key={u.user_id}>
                <td>{u.name}</td>
                <td>{u.total_calls}</td>
                <td>{fmtDuration(u.avg_duration_sec)}</td>
                <td>{u.last_active ? new Date(u.last_active).toLocaleDateString() : '—'}</td>
                <td>
                  <span className={`badge badge-${u.dominant_mood}`}>
                    {u.dominant_mood}
                  </span>
                </td>
              </tr>
            ))}
            {per_user.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>No data yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="analytics-version">{VERSION}</div>
    </AdminLayout>
  );
}
